import React from 'react'
import 'banner-pattern/dist/main.css'

type BannerVariant = 'error' | 'success' | 'info'

interface ValidationBannerProps {
  variant?: BannerVariant
  heading?: React.ReactNode
  message?: React.ReactNode
  className?: string
  fieldLevelErrors?: Record<string, string>
  fieldLabels?: Record<string, string>
  onFieldClick?: (fieldName: string) => void
  compact?: boolean
  iconSrc?: string
}

const scrollToField = (fieldName: string) => {
  try {
    const fieldElement = document.getElementById(fieldName) || document.getElementById(`date-input-${fieldName}`) || document.getElementById(`textarea-${fieldName}`)
    if (fieldElement) {
      fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      ;(fieldElement).focus()
    }
  } catch (e) {
    // ignore
    console.error('Error scrolling to field:', e)
  }
}

const ViewFieldButton: React.FC<{ fieldName: string; onFieldClick?: (f: string) => void }> = ({ fieldName, onFieldClick }) => (
  <button
    type="button"
    onClick={() => {
      scrollToField(fieldName)
      onFieldClick?.(fieldName)
    }}
    className='text-[#0074AD]! underline bg-transparent border-0 p-0 cursor-pointer'
  >
    View field
  </button>
)

const getIcon = (variant: BannerVariant) => {
  switch (variant) {
    case 'error':
      return '/assets/images/errorCircleSmall.svg'
    case 'success':
      return '/assets/images/checkmarkIcon.svg'
    default:
      return '/assets/images/informationIcon.svg'
  }
}

const getVariantStyle = (variant: BannerVariant, compact = true) => {
  const pad = compact ? '!p-3 !mb-3' : '!p-4 !mb-4'
  if (variant === 'error') return `${pad} !mt-3 !border-red-600`
  if (variant === 'success') return `${pad}  !border-green-600`
  return ''
}

const ValidationBanner: React.FC<ValidationBannerProps> = ({
  variant = 'info',
  heading,
  message,
  className = '',
  fieldLevelErrors,
  fieldLabels = {},
  onFieldClick,
  compact = true,
  iconSrc: customIconSrc,
}) => {
  const hasFieldErrors = fieldLevelErrors && Object.keys(fieldLevelErrors).length > 0
  if (!heading && !message && !hasFieldErrors) return null

  const iconSrc = customIconSrc || getIcon(variant)

  const variantStyle = getVariantStyle(variant, compact)
  const Wrapper: React.ElementType = variant === 'error' ? 'div' : 'output'
  const wrapperProps: Record<string, any> =
    variant === 'error'
      ? { role: 'alert', 'aria-live': 'assertive' }
      : { 'aria-live': 'polite' }

  return (
    <Wrapper
      className={`ds-banner ds-banner__bg--white ds-banner--icon ${variantStyle} ${className}`}
      data-ds-variant="basic"
      data-ds-version="1"
      data-ds-theme="vessel"
      {...wrapperProps}
      style={{width:"100%",padding:compact?'6px 10px':'8px 12px',boxSizing:'border-box'}}
    >
      <div className="ds-banner__icon-img" style={{ display: 'none' }}>
        <img src={iconSrc} alt="" style={{ width: 42, height: 42 }} />
      </div>
      <div className="ds-banner__container">
        <div className="ds-banner__main">
          {heading && (
            <div className="ds-banner__main-header--awareness flex items-center m-0! line-height-[1.4]! !gap-3">
              <img src={iconSrc} alt="" style={{ width: 42, height: 42, flexShrink: 0 }} />
              {heading}
            </div>
          )}

          {message && (
            <div className={`ds-banner__main-body text-sm leading-relaxed wrap-break-word  ${heading ? '' : 'flex items-center gap-3'}`} style={{ paddingLeft: heading ? '50px' : 0 ,marginTop: heading ? '1px' : 0}}>
              {/* items-center keeps the icon vertically centered against 1-line, 2-line, or multi-line text */}
              {!heading && <img src={iconSrc} alt="" style={{ width: 40, height: 40, flexShrink: 0 }} />}
              {message}
            </div>
          )}

          {hasFieldErrors && (
            <div className="ds-banner__main-body mt-2 text-sm">
              <ul className="my-0 pl-4">
                {Object.entries(fieldLevelErrors).map(([fieldName, errMsg]) => (
                  <li key={fieldName} className="mb-1 flex items-center gap-2">
                    <img src="/assets/images/errorCircleSmall.svg" alt="" style={{ width: 16, height: 16 }} />
                    <span className="font-medium">{(fieldLabels?.[fieldName]) || fieldName}</span>
                    <span className="text-sm text-red-600">{errMsg}</span>
                    <div className="ml-auto">
                      <ViewFieldButton fieldName={fieldName} onFieldClick={onFieldClick} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  )
}

export default ValidationBanner
